import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  output,
  signal,
} from '@angular/core';
import {
  UiBreadcrumbComponent,
  UiBreadcrumbItem,
} from '../../atoms/breadcrumb/breadcrumb.component';
import { UiTitleBarMode } from './title-bar.types';

export type { UiTitleBarMode } from './title-bar.types';

/**
 * Organismo: Barra de Título (Title Bar).
 *
 * Sección de encabezado reutilizable para data tables, paneles de control,
 * formularios y reportes. Siempre muestra título + breadcrumb en la zona
 * izquierda. La zona derecha es libre mediante `ng-content`, lo que permite
 * al consumidor proyectar filtros (`ui-select`, `ui-multiselect`,
 * `ui-date-input`) o botones de acción (`ui-button`) según el modo.
 *
 * El input `mode` actúa como semántica declarativa: comunica la intención
 * de uso sin bloquear qué se proyecta en la zona derecha.
 *
 * @example Data table
 * <ui-title-bar mode="data-table" title="Listado de leads" [breadcrumbItems]="items" />
 *
 * @example Formulario
 * <ui-title-bar mode="form" title="Nuevo lead" [breadcrumbItems]="items">
 *   <ui-button variant="secondary" (pressed)="onBack()">Regresar</ui-button>
 *   <ui-button variant="primary"   (pressed)="onSave()">Guardar</ui-button>
 * </ui-title-bar>
 *
 * @example Panel de control / Reporte
 * <ui-title-bar mode="control-panel" title="Dashboard" [breadcrumbItems]="items">
 *   <ui-select [options]="years" [(selected)]="year" placeholder="Año" [showLabel]="false" [showOptionalHint]="false" />
 *   <ui-select [options]="months" [(selected)]="month" placeholder="Mes" [showLabel]="false" [showOptionalHint]="false" />
 * </ui-title-bar>
 */
@Component({
  selector: 'ui-title-bar',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [UiBreadcrumbComponent],
  templateUrl: './title-bar.component.html',
  styleUrl: './title-bar.component.scss',
  host: {
    class: 'ui-title-bar-host',
    '[attr.data-mode]': 'mode()',
  },
})
export class UiTitleBarComponent {
  /**
   * Modo de uso. Controla semántica y ARIA del header; no restringe
   * el contenido proyectado en la zona derecha.
   * @default 'data-table'
   */
  readonly mode = input<UiTitleBarMode>('data-table');

  /** Título principal de la sección (obligatorio). */
  readonly title = input.required<string>();
  /** En móvil reemplaza al título. Vacío conserva `title` en todas las resoluciones. */
  readonly mobileTitle = input('');
  /** Segunda línea del título, solo en móvil y junto con `mobileTitle`. */
  readonly mobileSubtitle = input('');
  /** Modelo mostrado como metadato en la segunda línea móvil. */
  readonly mobileModel = input('');
  /** Unidad mostrada como metadato en la segunda línea móvil. */
  readonly mobileUnit = input('');

  /**
   * Lista de ítems del breadcrumb (sin incluir el ítem home).
   * El último ítem se marca automáticamente como página actual (no navegable).
   */
  readonly breadcrumbItems = input<readonly UiBreadcrumbItem[]>([]);

  /**
   * Ítem home del breadcrumb (icono o label inicial).
   * Si es `null`, no se muestra el home.
   */
  readonly breadcrumbHome = input<UiBreadcrumbItem | null>(null);

  /**
   * Etiqueta ARIA para el `<nav>` del breadcrumb.
   * @default 'Ruta de navegación'
   */
  readonly breadcrumbAriaLabel = input('Ruta de navegación');

  /**
   * Emite el `UiBreadcrumbItem` al hacer click en cualquier ítem
   * del breadcrumb que no sea el último (no en el ítem actual).
   */
  readonly breadcrumbItemSelect = output<UiBreadcrumbItem>();

  /**
   * En modo `report`, muestra la segunda fila de filtros.
   * @default false
   */
  readonly showSecondFilterRow = input(false);

  /**
   * Muestra el botón que despliega las filas de filtros adicionales.
   * Debe activarse únicamente cuando existe contenido proyectado en
   * `[data-ui-title-bar-more]`.
   * @default false
   */
  readonly showAdditionalFilterRows = input(false);

  protected readonly extraFiltersExpanded = signal(false);

  constructor() {
    effect(() => {
      if (!this.showSecondFilterRow() || !this.showAdditionalFilterRows()) {
        this.extraFiltersExpanded.set(false);
      }
    });
  }

  protected toggleExtraFilters(): void {
    this.extraFiltersExpanded.update((expanded) => !expanded);
  }
}
